/**
 * Resolves the three base drawings per aircraft type for the demo store:
 * /demo-drawings/drawings.json + PNGs next to it (static files on the app's
 * own origin, never the API). A real session gets its drawings from the API
 * instead (apiSource.ts, authenticated fetch → blob: URL). Anything missing
 * or broken falls back to the built-in silhouette, so the board never shows
 * an empty box.
 */
import { BASE_VIEWS, type BaseView } from '@fsv/core';
import { placeholderDrawing, type Drawing } from '@fsv/data';

interface ManifestEntry {
  blob?: string;
  width?: number;
  height?: number;
}
type Manifest = Record<string, Partial<Record<BaseView, ManifestEntry>>>;

const BASE = '/demo-drawings/';

let manifestPromise: Promise<Manifest> | null = null;

function loadManifest(): Promise<Manifest> {
  // Inside then(): a fetch that throws synchronously (blocked, stubbed) must become the fallback too.
  manifestPromise ??= Promise.resolve()
    .then(() => fetch(`${BASE}drawings.json`, { headers: { Accept: 'application/json' } }))
    .then(async (res) => {
      if (!res.ok) return {};
      const text = await res.text();
      // Vite's dev server and the SWA fallback answer unknown paths with index.html.
      if (text.trimStart().startsWith('<')) return {};
      return JSON.parse(text) as Manifest;
    })
    .catch(() => ({}));
  return manifestPromise;
}

/** Loads the image to prove it exists and to read its real size; null if it does not decode. */
function probeImage(url: string): Promise<{ width: number; height: number } | null> {
  if (typeof Image === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth > 0 ? { width: img.naturalWidth, height: img.naturalHeight } : null);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function blobUrl(blob: string): string {
  // Accept a bare file name or a path; never an absolute URL to another host (CSP: default-src 'self').
  const name = blob.replace(/^.*\//, '');
  return `${BASE}${encodeURIComponent(name)}`;
}

const typeCache = new Map<string, Promise<Record<BaseView, Drawing>>>();

/** Memoised per type: the board, the fleet cards and the nearby search all ask for the same drawings. */
export function loadDrawingsForType(typeCode: string): Promise<Record<BaseView, Drawing>> {
  const key = typeCode.toUpperCase();
  let pending = typeCache.get(key);
  if (!pending) {
    pending = resolveDrawings(typeCode);
    typeCache.set(key, pending);
  }
  return pending;
}

async function resolveDrawings(typeCode: string): Promise<Record<BaseView, Drawing>> {
  const manifest = await loadManifest();
  const entry = manifest[typeCode.toUpperCase()] ?? {};
  const results = await Promise.all(
    BASE_VIEWS.map(async (view): Promise<[BaseView, Drawing]> => {
      const meta = entry[view];
      const url = blobUrl(meta?.blob ?? `${typeCode.toLowerCase()}_${view}.png`);
      const probed = await probeImage(url);
      if (!probed) return [view, placeholderDrawing(typeCode, view)];
      // The manifest's size wins when present (it is what coordinates were recorded against);
      // the decoded size is the fallback.
      const width = meta?.width && meta.width > 0 ? meta.width : probed.width;
      const height = meta?.height && meta.height > 0 ? meta.height : probed.height;
      return [view, { url, width, height, placeholder: false }];
    }),
  );
  return Object.fromEntries(results) as Record<BaseView, Drawing>;
}

/** For tests and the "reset" action: forget the cached manifest. */
export function resetDrawingCache(): void {
  manifestPromise = null;
  typeCache.clear();
}
